import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getStoreTotalsDataColumns} from "../../Components/StoreTotalsDataColumns";
import TotalsSearchResults from "../../Components/TotalsSearchResults";
import PropTypes from "prop-types";
import {buildStoreTotalsModel} from "./buildStoreTotalsModel";
import {buildConfigOverlay} from "./buildConfigOverlay";
import {buildOutlierIndex} from "./buildOutlierIndex";
import {getStoreDataAsCsv} from "./StoreTotalsTotalDollarsCsv";
import {displayFunctions} from "./StoreTotalsDataSelections";
import {getStyleForOutlierFunction} from "./getStyleForOutlierFunction";
import StoreTotalsDollarsKey from "./StoreTotalsDollarsKey";
import NameValueLabels from "../../../Components/NameValueLabels";
import ArchiveFilenameModal from "./ArchiveFilenamesModal";
import {fetchStoreTotalsData} from "./fetchStoreTotalsData";
import {makeApolloClientCall} from "../../../../Api/makeApolloApiCall";
import {LoadingWrapper} from "../../../Components/LoadingWrapper";
import {StoreTotalsContextMenuProvider} from "./StoreTotalsContextMenuProvider";

const CONTEXT_MENU_ID = 'total-dollars-context-menu';

export const StoreTotalsTotalDollars = (
    {
        selectedData,
        retailerId,
        percentThreshold,
        onPercentThresholdChange,
        dataFormat,
        onDataFormatChange,
        queries,
        initialCellValueClicked,
        includeFillOnly,
        salesFilters,
        fillWatermark,
        promoWatermark,
        reloadData
    }
) => {
    const displayTabs = [
        {displayKey: 'value', label: 'value'},
        {displayKey: 'percentDifference', label: '% Difference'},
    ]
    const [cellValueClicked, setCellValueClicked] = useState(initialCellValueClicked);
    const pageSizeRef = React.useRef(10);
    const [loadedData, setLoadedData] = useState(undefined);
    const [configRefreshRequest, setConfigRefreshRequest] = useState(undefined);
    const [refreshedConfigs, setRefreshedConfigs] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [reloading, setReloading] = useState(false);
    const [error, setError] = useState(false);

    const rawData = loadedData?.rawData;
    const loadedSelectedData = loadedData?.selectedData ?? selectedData;
    const loadedIncludeFillOnly = loadedData?.includeFillOnly ?? includeFillOnly;
    const totalsDataSelection = displayFunctions[loadedSelectedData];
    const displayDataColumnFunction = dataFormat === 'percentDifference'
        ? totalsDataSelection.displayDiffFromAverageFunction
        : totalsDataSelection.displayFunction;

    const reactTableXCoordinateRef = React.useRef(0);
    React.useEffect(() => {
        reactTableXCoordinateRef.current = 0;
    }, [retailerId])

    const cellValueClickedCallback = useCallback((store, weekEndData, source) => setCellValueClicked({store, weekEndData, source}), []);
    const refreshConfigs = useCallback(() => setConfigRefreshRequest({query: queries.configs}), [queries]);

    useEffect(() => {
        let ignore = false;
        setLoadedData(undefined);
        setRefreshedConfigs(undefined);
        setConfigRefreshRequest(undefined);
        setError(false);
        setReloading(false);
        setLoading(true);
        fetchStoreTotalsData(queries)
            .then((results) => {
                if (!ignore) setLoadedData({rawData: results, selectedData, includeFillOnly});
            })
            .catch(() => {
                if (!ignore) setError(true);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [queries, reloadData, selectedData, includeFillOnly]);

    useEffect(() => {
        if (!configRefreshRequest) return;
        let ignore = false;
        setError(false);
        setReloading(true);
        makeApolloClientCall(configRefreshRequest.query, false)
            .then((results) => {
                if (!ignore) setRefreshedConfigs(results);
            })
            .catch(() => {
                if (!ignore) setError(true);
            })
            .finally(() => {
                if (!ignore) setReloading(false);
            });
        return () => {
            ignore = true;
        };
    }, [configRefreshRequest]);

    const model = useMemo(() => rawData && buildStoreTotalsModel(rawData, loadedIncludeFillOnly, totalsDataSelection.valueKey),
        [rawData, loadedIncludeFillOnly, totalsDataSelection.valueKey]);
    const configOverlay = useMemo(() => rawData && buildConfigOverlay({...rawData, ...refreshedConfigs}), [rawData, refreshedConfigs]);
    const outlierIndex = useMemo(() => rawData && buildOutlierIndex(rawData.averages, loadedSelectedData), [rawData, loadedSelectedData]);

    const styleForOutlierFunction = useMemo(() => getStyleForOutlierFunction(totalsDataSelection, percentThreshold),
        [totalsDataSelection, percentThreshold]);
    const tableMeta = useMemo(() => ({
        configOverlay,
        displayDataFunction: displayDataColumnFunction,
        styleForOutlierFunction
    }), [configOverlay, displayDataColumnFunction, styleForOutlierFunction]);

    const columns = useMemo(() => model ? getStoreTotalsDataColumns({
        retailerId: retailerId,
        model: model,
        outlierIndex: outlierIndex,
        contextMenuId: CONTEXT_MENU_ID,
        selectedData: loadedSelectedData,
        setTimestamp: refreshConfigs,
        cellValueClicked: cellValueClickedCallback,
        fillWatermark: fillWatermark,
        promoWatermark: promoWatermark
    }) : [], [model, outlierIndex, retailerId, loadedSelectedData, cellValueClickedCallback, refreshConfigs, fillWatermark, promoWatermark]);

    const applyThreshold = (event) => onPercentThresholdChange(event.target.value === '' ? undefined : Number(event.target.value));

    return <StoreTotalsContextMenuProvider><div>
        <div className="tab-radio-buttons">
            {displayTabs.map((displayObject => {
                    return <div key={displayObject.displayKey} className="tab-radio-button-container">
                        <input id={`radio-${displayObject.displayKey}`}
                               type="radio"
                               className="tab-radio-button"
                               name={"file-type-radio"}
                               checked={displayObject.displayKey === dataFormat}
                               onChange={() => onDataFormatChange(displayObject.displayKey)}
                        />
                        <label htmlFor={`radio-${displayObject.displayKey}`}
                               className="radio-inline">{displayObject.label}</label>
                    </div>
                })
            )}
        </div>
        <div className={'store-totals-toolbar'}>
            <div className={'id-input store-totals-threshold'}>
                <label htmlFor={'percent_threshold'} className={'input-group-label'} data-testid={'label-percent-threshold'}>Threshold Percent to Flag:</label>
                <input
                    data-testid={'input-percent-threshold'}
                    id={'percent_threshold'}
                    className={'input-group-field'}
                    type={'number'}
                    min={0}
                    defaultValue={percentThreshold ?? ''}
                    onBlur={applyThreshold}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') applyThreshold(event);
                    }}
                />
            </div>
            <div className={"total-dollars-key"}>
                <NameValueLabels labelValuePairs={[{label: "Store Totals Table", value: <StoreTotalsDollarsKey/>}]}/>
            </div>
            {model && <button type={'button'}
                              className={'download-csv hollow button'}
                              data-testid={'download-csv'}
                              onClick={() => getStoreDataAsCsv(retailerId, model, outlierIndex, loadedSelectedData)}>
                {'Download CSV'}
            </button>}
        </div>
        <LoadingWrapper loading={loading}>
            {reloading && <div className={'refresh-data-message'} data-testid={'refresh-data-div'}>...Refreshing Configs</div>}
            {model && <TotalsSearchResults data={model.rows}
                                           pageSize={pageSizeRef.current}
                                           updatePageSize={(pageSize) => {
                                               pageSizeRef.current = pageSize;
                                           }}
                                           columns={columns}
                                           defaultSortedId={"storeId"}
                                           reactTableXCoordinateRef={reactTableXCoordinateRef}
                                           meta={tableMeta}
            />}
            {error && <div className={'error-message'} data-testid={'error-div'}>Error loading data</div>}
        </LoadingWrapper>
        {cellValueClicked && <ArchiveFilenameModal
            closeModal={() => setCellValueClicked(undefined)}
            date={cellValueClicked.weekEndData.date}
            retailerId={retailerId}
            source={cellValueClicked.source}
            storeId={cellValueClicked.store.storeId}
            storeName={cellValueClicked.store.storeName}
            salesFilters={{...salesFilters, includeFillOnly: loadedIncludeFillOnly}}
        />}
    </div></StoreTotalsContextMenuProvider>;
};

StoreTotalsTotalDollars.propTypes = {
    queries: PropTypes.object,
    dataFormat: PropTypes.string,
    initialCellValueClicked: PropTypes.object,
    onDataFormatChange: PropTypes.func,
    percentThreshold: PropTypes.number,
    onPercentThresholdChange: PropTypes.func.isRequired,
    retailerId: PropTypes.string.isRequired,
    selectedData: PropTypes.string.isRequired,
    includeFillOnly: PropTypes.bool,
    salesFilters: PropTypes.shape({
        includePlu: PropTypes.bool,
        includeIfps: PropTypes.bool,
        productSnapshotOnly: PropTypes.bool
    }).isRequired,
    fillWatermark: PropTypes.array,
    promoWatermark: PropTypes.array,
    reloadData: PropTypes.any
}

export default React.memo(StoreTotalsTotalDollars);
